import { useEffect, useMemo, useRef, useState } from 'react';
import { useDebounce } from '@aimsos/hooks';

import { checkEntityExists, mapApiExistsToUi } from '../../../services/entityExists.js';

const DEBOUNCE_MS = 400;
const IDLE = Object.freeze({ exists: false, matches: [], failed: false });

/**
 * Asks "does this record already exist?" as the user types the identifying
 * field (email / domain).
 *
 * `checking` is true from the first keystroke, not from when the request leaves:
 * during the debounce the previous answer is stale, and a step that advanced in
 * that window would let a duplicate through. A failed check never blocks — the
 * create call answers 409 anyway — it is reported as `failed` so a screen can
 * choose to say so.
 */
export function useRecordExists({ entityType, key, enabled = true } = {}) {
  const keyString = useMemo(() => JSON.stringify(key ?? {}), [key]);
  const debouncedKeyString = useDebounce(keyString, DEBOUNCE_MS);
  const [answer, setAnswer] = useState({ ...IDLE, forKey: null });
  const [inFlight, setInFlight] = useState(false);
  const fetchIdRef = useRef(0);

  useEffect(() => {
    if (!enabled) return undefined;
    const requestId = ++fetchIdRef.current;
    setInFlight(true);

    (async () => {
      try {
        const { body } = await checkEntityExists({ entityType, key: JSON.parse(debouncedKeyString) });
        if (requestId !== fetchIdRef.current) return;
        setAnswer({ ...mapApiExistsToUi(body), failed: false, forKey: debouncedKeyString });
      } catch {
        if (requestId !== fetchIdRef.current) return;
        setAnswer({ ...IDLE, failed: true, forKey: debouncedKeyString });
      } finally {
        if (requestId === fetchIdRef.current) setInFlight(false);
      }
    })();

    return () => {
      fetchIdRef.current += 1;
    };
  }, [enabled, entityType, debouncedKeyString]);

  return useMemo(() => {
    if (!enabled) return { checking: false, ...IDLE };
    const debouncing = keyString !== debouncedKeyString;
    return {
      checking: debouncing || inFlight || answer.forKey !== debouncedKeyString,
      exists: answer.forKey === keyString && answer.exists,
      matches: answer.forKey === keyString ? answer.matches : IDLE.matches,
      failed: answer.forKey === keyString && answer.failed,
    };
  }, [enabled, keyString, debouncedKeyString, inFlight, answer]);
}

export default useRecordExists;
